<template>
  <div class="user-avatar-wrapper" :class="`avatar-${size}`">
    <!-- Avatar com imagem -->
    <img
      v-if="validatedImageUrl"
      :src="validatedImageUrl"
      :alt="altText"
      class="avatar-image"
      @error="handleImageError"
    />

    <!-- Avatar placeholder (primeira letra do username) -->
    <div v-else class="avatar-placeholder">
      <span class="avatar-letter">{{ firstLetter }}</span>
    </div>

    <!-- Badge de verificação (opcional) -->
    <div v-if="showBadge" class="avatar-badge">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M5 13l4 4L19 7"
        />
      </svg>
    </div>
  </div>
</template>

<script>
import { computed, ref } from "vue";

export default {
  name: "UserAvatar",
  props: {
    // Objeto user com profile_image_url e username
    user: {
      type: Object,
      default: null,
    },
    // Tamanho do avatar: 'xs', 'sm', 'md', 'lg', 'xl'
    size: {
      type: String,
      default: "md",
      validator: (value) => ["xs", "sm", "md", "lg", "xl"].includes(value),
    },
    // Mostrar badge de verificação
    showBadge: {
      type: Boolean,
      default: false,
    },
    // Texto alternativo customizado
    alt: {
      type: String,
      default: "",
    },
  },
  setup(props) {
    const imageError = ref(false);

    // Valida e retorna URL de imagem segura com VALIDAÇÃO RIGOROSA
    const validatedImageUrl = computed(() => {
      // Se teve erro ao carregar, não tenta de novo
      if (imageError.value) return null;

      const rawUrl = props.user?.profile_image_url;
      if (!rawUrl) return null;

      // Se for base64, valida RIGOROSAMENTE antes de usar
      if (rawUrl.startsWith("data:image/")) {
        try {
          // 1. Verificar formato correto (deve ter vírgula separando header do data)
          const parts = rawUrl.split(",");
          if (parts.length !== 2) {
            console.warn(
              `⚠️ [UserAvatar] Base64 inválido (sem vírgula) - User: ${props.user?.username}`
            );
            return null;
          }

          const base64Data = parts[1];

          // 2. Verificar se não está vazio
          if (!base64Data) {
            console.warn(
              `⚠️ [UserAvatar] Base64 vazio após header - User: ${props.user?.username}`
            );
            return null;
          }

          // 3. Verificar tamanho mínimo (imagem real deve ter >1KB = ~1333 chars base64)
          const MIN_BASE64_SIZE = 1000;
          if (base64Data.length < MIN_BASE64_SIZE) {
            console.warn(
              `⚠️ [UserAvatar] Base64 truncado (${base64Data.length} chars) - User: ${props.user?.username}`
            );
            return null;
          }

          // 4. Verificar se contém apenas caracteres válidos (primeiros 100 chars)
          const base64Regex = /^[A-Za-z0-9+/=]+$/;
          const sampleChars = base64Data.substring(0, 100);
          if (!base64Regex.test(sampleChars)) {
            console.warn(
              `⚠️ [UserAvatar] Base64 com caracteres inválidos - User: ${props.user?.username}`
            );
            return null;
          }

          // 5. Verificar tamanho máximo (~10MB = 13.3M chars)
          const MAX_BASE64_SIZE = 13300000;
          if (base64Data.length > MAX_BASE64_SIZE) {
            console.warn(
              `⚠️ [UserAvatar] Base64 muito grande (${base64Data.length} chars) - User: ${props.user?.username}`
            );
            return null;
          }

          // ✅ Passou em todas as validações
          return rawUrl;
        } catch (error) {
          console.error(
            `❌ [UserAvatar] Erro ao validar base64 - User: ${props.user?.username}`,
            error
          );
          return null;
        }
      }

      // Se já for uma URL completa (https/http), usa direto
      if (rawUrl.startsWith("http")) {
        return rawUrl;
      }

      // Se for uma URL relativa (/uploads/...), constrói com a base da API
      const apiBase =
        process.env.VUE_APP_API_URL || "http://localhost:3000/api";
      const baseUrl = apiBase.replace("/api", "");
      const fullUrl = rawUrl.startsWith("/")
        ? baseUrl + rawUrl
        : baseUrl + "/" + rawUrl;
      return fullUrl;
    });

    // Primeira letra do username para placeholder
    const firstLetter = computed(() => {
      const username = props.user?.username || props.user?.name || "?";
      return username.charAt(0).toUpperCase();
    });

    // Texto alternativo
    const altText = computed(() => {
      if (props.alt) return props.alt;
      return `Avatar de ${props.user?.username || "usuário"}`;
    });

    // Handler para erro de carregamento de imagem
    const handleImageError = () => {
      console.warn(
        `⚠️ [UserAvatar] Erro ao carregar imagem para: ${props.user?.username}`
      );
      imageError.value = true;
    };

    return {
      validatedImageUrl,
      firstLetter,
      altText,
      handleImageError,
    };
  },
};
</script>

<style scoped>
/* Container do Avatar */
.user-avatar-wrapper {
  position: relative;
  display: inline-block;
  flex-shrink: 0;
}

/* Avatar com Imagem */
.avatar-image {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  background: #f3f4f6;
}

/* Avatar Placeholder */
.avatar-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-letter {
  color: white;
  font-weight: 700;
  user-select: none;
}

/* Badge de Verificação */
.avatar-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  background: #10b981;
  border-radius: 50%;
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Tamanhos do Avatar */

/* Extra Small - 24px */
.avatar-xs {
  width: 24px;
  height: 24px;
}

.avatar-xs .avatar-letter {
  font-size: 0.625rem;
}

.avatar-xs .avatar-badge {
  width: 10px;
  height: 10px;
  border-width: 1px;
  bottom: -1px;
  right: -1px;
}

.avatar-xs .avatar-badge svg {
  width: 6px;
  height: 6px;
  stroke-width: 3;
}

/* Small - 32px */
.avatar-sm {
  width: 32px;
  height: 32px;
}

.avatar-sm .avatar-letter {
  font-size: 0.875rem;
}

.avatar-sm .avatar-badge {
  width: 12px;
  height: 12px;
  border-width: 2px;
}

.avatar-sm .avatar-badge svg {
  width: 8px;
  height: 8px;
  stroke-width: 3;
}

/* Medium - 40px (default) */
.avatar-md {
  width: 40px;
  height: 40px;
}

.avatar-md .avatar-letter {
  font-size: 1rem;
}

.avatar-md .avatar-badge {
  width: 14px;
  height: 14px;
  border-width: 2px;
}

.avatar-md .avatar-badge svg {
  width: 10px;
  height: 10px;
  stroke-width: 2.5;
}

/* Large - 56px */
.avatar-lg {
  width: 56px;
  height: 56px;
}

.avatar-lg .avatar-letter {
  font-size: 1.5rem;
}

.avatar-lg .avatar-badge {
  width: 18px;
  height: 18px;
  border-width: 2px;
}

.avatar-lg .avatar-badge svg {
  width: 12px;
  height: 12px;
  stroke-width: 2.5;
}

/* Extra Large - 80px */
.avatar-xl {
  width: 80px;
  height: 80px;
}

.avatar-xl .avatar-letter {
  font-size: 2rem;
}

.avatar-xl .avatar-badge {
  width: 24px;
  height: 24px;
  border-width: 3px;
}

.avatar-xl .avatar-badge svg {
  width: 16px;
  height: 16px;
  stroke-width: 2.5;
}

/* Animação de Hover */
.user-avatar-wrapper {
  transition: transform 0.2s ease;
}

.user-avatar-wrapper:hover {
  transform: scale(1.05);
}
</style>
